import React, { useEffect, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import {
  CContainer,
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
} from "@coreui/react";
import { CChartLine } from "@coreui/react-chartjs";
import settingsStore from "../../store/SettingsStore";
import periodPrefsStore from "../../store/PeriodPrefsStore";
import PeriodFilters from "../UI/PeriodFilters/PeriodFilters";
import ServerSelect from "../UI/PeriodFilters/ServerSelect";
import ErrorAlert from "../UI/Status/ErrorAlert";
import { SkeletonChart, SkeletonTable } from "../UI/Skeleton/Skeleton";
import { getCostMargin } from "../../api/reportsApi";
import { fmtInt } from "../../utils/chartHelpers";
import { logReportView } from "../../api/reportLogApi";
import { formatYmd } from "../../utils/dateRange";

function DishCostTable({ title, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="mb-2">
      <div className="small fw-semibold text-body-secondary mb-1">{title}</div>
      <CTable small hover responsive className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell>Блюдо</CTableHeaderCell>
            <CTableHeaderCell>Кол-во</CTableHeaderCell>
            <CTableHeaderCell>Выручка</CTableHeaderCell>
            <CTableHeaderCell>Фудкост</CTableHeaderCell>
            <CTableHeaderCell>Маржа</CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {rows.slice(0, 10).map((d, i) => (
            <CTableRow key={`${d.name}-${i}`}>
              <CTableDataCell>{d.name}</CTableDataCell>
              <CTableDataCell>{fmtInt(d.qty)}</CTableDataCell>
              <CTableDataCell>{fmtInt(d.revenue)} ₽</CTableDataCell>
              <CTableDataCell>{d.cost_percent}%</CTableDataCell>
              <CTableDataCell>{d.margin_percent}%</CTableDataCell>
            </CTableRow>
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}

const CostMarginPage = observer(() => {
  const [selectedServer, setSelectedServer] = useState(periodPrefsStore.selectedServer);
  const [fromDate, setFromDate] = useState(periodPrefsStore.fromDate);
  const [toDate, setToDate] = useState(periodPrefsStore.toDate);
  const [quickRange, setQuickRange] = useState(periodPrefsStore.quickRange);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (from = fromDate, to = toDate) => {
      if (!selectedServer || !from || !to) return;
      setLoading(true);
      setError(null);
      try {
        setResult(await getCostMargin(selectedServer, from, to));
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    },
    [selectedServer, fromDate, toDate],
  );

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      if (!fromDate || !toDate) {
        const today = new Date();
        const past = new Date(today);
        past.setDate(today.getDate() - 14);
        setFromDate(formatYmd(past));
        setToDate(formatYmd(today));
      }
      logReportView({ event: "report_view", reportKey: "cost_margin", reportTitle: "Себестоимость и фудкост", userId, path: "/cost-margin" });
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (!selectedServer) setSelectedServer(String(settingsStore.servers[0].id));
  }, [settingsStore.servers, selectedServer]);

  useEffect(() => {
    if (selectedServer) periodPrefsStore.setServer(selectedServer);
  }, [selectedServer]);

  useEffect(() => {
    if (selectedServer && fromDate && toDate) load(fromDate, toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServer, fromDate, toDate]);

  const handleRefresh = (from, to) => {
    periodPrefsStore.setPeriod(from, to, quickRange);
    load(from, to);
  };

  const trend = result?.daily_cost_trend || [];

  return (
    <CContainer style={{ flex: "1", marginTop: "120px" }}>
      <h4 className="mb-3">Себестоимость и фудкост</h4>
      <p className="text-body-secondary small">
        Динамика среднего фудкоста по ресторану в целом и блюда с самым высоким/низким фудкостом за период.
      </p>
      <ServerSelect servers={settingsStore.servers} value={selectedServer} onChange={(e) => setSelectedServer(e.target.value)} />
      <PeriodFilters
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
        onRefresh={handleRefresh}
        quickRange={quickRange}
        onQuickRangeChange={setQuickRange}
        disabled={loading}
        loading={loading}
      />
      <ErrorAlert error={error} />

      <CCard className="mt-3 mb-3">
        <CCardHeader className="fw-semibold">
          Динамика фудкоста{result ? ` · в среднем ${result.avg_cost_percent}%` : ""}
        </CCardHeader>
        <CCardBody>
          {loading && !result && <SkeletonChart height={180} />}
          {!loading && trend.length > 0 && (
            <div style={{ maxHeight: 220 }}>
              <CChartLine
                data={{
                  labels: trend.map((d) => d.date),
                  datasets: [
                    {
                      label: "Фудкост, %",
                      borderColor: "#E46651",
                      backgroundColor: "rgba(228,102,81,0.15)",
                      data: trend.map((d) => d.cost_percent),
                      tension: 0.3,
                    },
                  ],
                }}
                options={{ maintainAspectRatio: false, plugins: { legend: { display: false } } }}
              />
            </div>
          )}
        </CCardBody>
      </CCard>

      <CRow className="g-3">
        <CCol xs={12} lg={6}>
          <CCard className="h-100">
            <CCardBody>
              {loading && !result ? (
                <SkeletonTable rows={5} />
              ) : (
                <DishCostTable title="Самый высокий фудкост" rows={result?.highest_cost_dishes} />
              )}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol xs={12} lg={6}>
          <CCard className="h-100">
            <CCardBody>
              {loading && !result ? (
                <SkeletonTable rows={5} />
              ) : (
                <DishCostTable title="Самый низкий фудкост" rows={result?.lowest_cost_dishes} />
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </CContainer>
  );
});

export default CostMarginPage;
